<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import RichText from "$lib/molecules/RichText.svelte";
  import Button from "$lib/atoms/Button.svelte";
  import type { TextCardStoryblok } from "$types/components";
  import StoryblokComponent from "$lib/StoryblokComponent.svelte";
  import { IconType } from "$types/atoms";
  import { displayType } from "$lib/utils/breakpoints";

  export let blok: TextCardStoryblok;
  export let contentClass = "";
  export let hero = false;
  export let heroHorizontal = false;

  $: textAlignment =
    $displayType === "mobile"
      ? blok.textAlignment
      : blok.textAlignmentDesktop || blok.textAlignment;
  $: nonLinkButton =
    blok.buttons?.length === 1 && blok.buttons[0].component !== "buttonLink";
  $: bodyFontSize =
    hero || heroHorizontal
      ? "type:hero-subtitle"
      : blok.bodyFontSize === "medium"
        ? "type:body-medium"
        : blok.bodyFontSize === "large"
          ? "type:body-large"
          : blok.bodyFontSize === "extra-large"
            ? "type:body-subtitle"
            : "";
</script>

<div
  class={`
    text-card
    flex
    flex-col
    ${textAlignment}
    ${blok.textColor}
  `}
  class:w-full={heroHorizontal}
  class:!space-y-2={!heroHorizontal && blok.spacing === "12"}
  class:md:!space-y-[12px]={!heroHorizontal && blok.spacing === "12"}
  class:!space-y-[7px]={!heroHorizontal && blok.spacing === "7"}
  class:!space-y-[24px]={!heroHorizontal &&
    blok.spacing !== "12" &&
    blok.spacing !== "7"}
  class:items-center={heroHorizontal || textAlignment === "text-center"}
  class:items-end={!heroHorizontal && textAlignment === "text-right"}
  class:items-start={!heroHorizontal &&
    textAlignment !== "text-right" &&
    textAlignment !== "text-center"}
  class:space-y-[24px]={!heroHorizontal}
  class:md:flex-row={heroHorizontal}
  class:gap-[24px]={heroHorizontal}
  class:mt-4={blok.topMargin === "small"}
  class:mt-8={blok.topMargin === "medium"}
  class:mt-20={blok.topMargin === "large"}
  class:mb-4={blok.bottomMargin === "small"}
  class:mb-8={blok.bottomMargin === "medium"}
  class:mb-20={blok.bottomMargin === "large"}
  use:storyblokEditable={blok}
>
  <!-- Subheading -->
  {#if blok?.subheading?.length}
    <h6 class={`type:section-title ${blok.textColor}`}>
      {@html blok.subheading}
    </h6>
  {/if}

  <!-- Heading -->
  {#if blok?.heading?.length}
    <svelte:element
      this={hero || blok.headingLevel === "h1" ? "h1" : "h2"}
      class={`
        text-card-heading
        type:${blok.headingFontSize}
        ${blok.textColor}
        ${
          // Note that this doesn't work reliably
          blok.headingMaxWidth ? `md:max-w-[${blok.headingMaxWidth}]` : ""
        }
      `}
      class:md:w-1_12={blok.contentWidth === "1/12"}
      class:md:w-2_12={blok.contentWidth === "2/12"}
      class:md:w-3_12={blok.contentWidth === "3/12"}
      class:md:w-4_12={blok.contentWidth === "4/12"}
      class:md:w-5_12={blok.contentWidth === "5/12"}
      class:md:w-6_12={blok.contentWidth === "6/12"}
      class:md:w-7_12={blok.contentWidth === "7/12"}
      class:md:w-8_12={blok.contentWidth === "8/12"}
      class:md:w-9_12={blok.contentWidth === "9/12"}
      class:md:w-10_12={blok.contentWidth === "10/12"}
      class:md:w-11_12={blok.contentWidth === "11/12"}
      class:md:w-12_12={blok.contentWidth === "12/12"}
    >
      {@html blok.heading}
    </svelte:element>
  {/if}

  <!-- Content -->
  {#if blok.content?.content?.[0]?.content}
    <div
      class={`
        text-card-content
        ${
          // Note that this doesn't work reliably
          blok.headingMaxWidth ? `md:max-w-[${blok.headingMaxWidth}]` : ""
        }
      `}
      class:type:heroHorizontal-subtitle={heroHorizontal}
      class:md:w-[70%]={heroHorizontal}
      class:md:max-w-[790px]={heroHorizontal}
      class:mr-0={blok.rightMargin === "none"}
      class:mr-3={blok.rightMargin !== "none"}
      class:md:w-1_12={blok.contentWidth === "1/12"}
      class:md:w-2_12={blok.contentWidth === "2/12"}
      class:md:w-3_12={blok.contentWidth === "3/12"}
      class:md:w-4_12={blok.contentWidth === "4/12"}
      class:md:w-5_12={blok.contentWidth === "5/12"}
      class:md:w-6_12={blok.contentWidth === "6/12"}
      class:md:w-7_12={blok.contentWidth === "7/12"}
      class:md:w-8_12={blok.contentWidth === "8/12"}
      class:md:w-9_12={blok.contentWidth === "9/12"}
      class:md:w-10_12={blok.contentWidth === "10/12"}
      class:md:w-11_12={blok.contentWidth === "11/12"}
      class:md:w-12_12={blok.contentWidth === "12/12"}
    >
      <RichText {blok} class={`${contentClass} ${bodyFontSize || ""}`} />
    </div>
  {/if}

  <!-- Buttons -->
  {#if blok.buttons && blok.buttons.length > 0}
    <div
      class="flex"
      class:flex-auto={heroHorizontal}
      class:w-full={heroHorizontal}
      class:md:w-auto={heroHorizontal}
      class:flex-wrap={!heroHorizontal}
      class:justify-end={heroHorizontal}
      class:self-stretch={!heroHorizontal && nonLinkButton}
      class:md:pt-[8px]={!heroHorizontal && blok.extraButtonSpacing === "8"}
      class:-ml-5={!heroHorizontal}
      class:-mb-3={!heroHorizontal}
    >
      {#each blok.buttons as button}
        <div
          class="flex"
          class:flex-auto={!heroHorizontal}
          class:pl-5={!heroHorizontal}
          class:pb-3={!heroHorizontal && !nonLinkButton}
          class:pb-[48px]={!heroHorizontal && nonLinkButton}
          class:self-stretch={!heroHorizontal && nonLinkButton}
          class:w-full={heroHorizontal}
          class:md:w-[182px]={heroHorizontal}
          class:md:max-w-[182px]={heroHorizontal}
        >
          {#if button.component === "buttonLink"}
            <div class="w-full" use:storyblokEditable={button}>
              <Button
                class="!w-[-webkit-fill-available]"
                href={button.url}
                target={button.url?.target}
                color={button.color
                  ? button.color
                  : blok.textColor && blok.textColor !== "text-inherit"
                    ? blok.textColor.replace("text-", "")
                    : "black"}
                rounded={button.rounded}
                icon={button.arrow ? IconType.ARROW_RIGHT : undefined}
              >
                {button.text}
              </Button>
            </div>
          {:else}
            <div class="flex grow gap-4" use:storyblokEditable={button}>
              <StoryblokComponent blok={button} />
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style lang="postcss">
  .text-card {
    &.text-inherit {
      .text-card-heading {
        color: rgb(var(--color-text-dark));
      }
    }

    &.text-black {
      .text-card-content .rich-text {
        color: rgb(var(--color-text-dark));
      }
    }
  }
</style>
